import { FormikProps } from 'formik'
import * as yup from 'yup'

import { isICCValid } from '../helpers'
import { isValidDate, minAge, maxAge, parseDate, isValidDocIdDate } from '../helpers/date.helpers'
import { isValidNIFControlDigit, isValidNIEControlDigit } from '../helpers/document.helpers'
import { isValidBankAccountControlDigit, isValidIBANControlDigit } from '../helpers/payment.helpers'

export const PHONE_NUMBER_REGEX = /^[6|7|8|9][0-9]{8}$/
export const MOBILE_NUMBER_REGEX = /^[6|7][0-9]{8}$/
export const LAND_NUMBER_REGEX = /^[8|9][0-9]{8}$/
export const ALPHA_REGEX = /^([^0-9]*)$/
export const POSTAL_CODE_REGEX = /^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/
export const CIF_REGEX = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/
// eslint-disable-next-line no-useless-escape
export const EMAIL_REGEX = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+((?!(con|coom))[a-zA-Z]{2,})))$/
export const NUMBER_REGEX = /(^[0-9]+$|^$)/

export default class StringSchema extends yup.string {
  phoneNumber(msg?: string) {
    return this.test({
      name: 'phoneNumber',
      exclusive: true,
      message: msg || 'Introduce un número de teléfono válido',
      test: (value) => value === undefined || PHONE_NUMBER_REGEX.test(value),
    })
  }

  mobileNumber(msg?: string) {
    return this.test({
      name: 'mobileNumber',
      exclusive: true,
      message: msg || 'El número de teléfono móvil no es válido',
      test: (value) => MOBILE_NUMBER_REGEX.test(value),
    })
  }

  landNumber(msg?: string) {
    return this.test({
      name: 'landNumber',
      exclusive: true,
      message: msg || 'El número de teléfono fijo no es válido',
      test: (value) => LAND_NUMBER_REGEX.test(value),
    })
  }

  uniqueNumber(form?: React.RefObject<FormikProps<any>>) {
    return this.test({
      name: 'uniqueNumber',
      exclusive: true,
      message: 'Número móvil duplicado',
      test: (value: string) => {
        if (!form) return true
        const lines = form?.current?.values?.lines || []
        return lines.filter(({ phoneNumber }: any) => phoneNumber === value).length < 2
      },
    })
  }

  iccid(msg?: string) {
    return this.test({
      name: 'iccid',
      exclusive: true,
      message: msg || 'El ICC no es válido',
      test: (value) => isICCValid(value),
    })
  }

  alpha(msg?: string) {
    return this.test({
      name: 'alpha',
      exclusive: true,
      message: msg || 'No puedes introducir números',
      test: (value) => ALPHA_REGEX.test(value),
    })
  }

  isValidDocIdDate(msg?: string) {
    return this.test({
      name: 'isValidDocIdDate',
      exclusive: true,
      message: msg || 'La fecha tiene que ser posterior a hoy',
      test: (value) => isValidDocIdDate(value),
    })
  }

  isValidDate(msg?: string) {
    return this.test({
      name: 'isValidDate',
      exclusive: true,
      message: msg || 'La fecha no es valida',
      test: (value) => isValidDate(value),
    })
  }

  isNotEmptyString(msg?: string) {
    return this.test({
      name: 'isEmptyString',
      exclusive: true,
      message: msg || 'Campo obligatorio',
      test: (value) => value !== '',
    })
  }

  minAge(age: number, msg?: string) {
    return this.test({
      name: 'minAge',
      exclusive: true,
      message: msg || 'Edad no valida',
      test: (value) => minAge(parseDate(value), age),
    })
  }

  maxAge(age: number, msg?: string) {
    return this.test({
      name: 'maxAge',
      exclusive: true,
      message: msg || 'Edad no valida',
      test: (value) => maxAge(parseDate(value), age),
    })
  }

  CIFControlDigit(msg?: string) {
    return this.test({
      name: 'CIF',
      exclusive: true,
      message: msg || 'El CIF no es válido',
      test: (value) => CIF_REGEX.test(value),
    })
  }

  NIFControlDigit(msg?: string) {
    return this.test({
      name: 'NIF',
      exclusive: true,
      message: msg || 'El NIF no es válido',
      test: (value) => isValidNIFControlDigit(value),
    })
  }

  NIEControlDigit(msg?: string) {
    return this.test({
      name: 'NIE',
      exclusive: true,
      message: msg || 'El NIE no es válido',
      test: (value) => isValidNIEControlDigit(value),
    })
  }

  IBANControlDigit(msg?: string) {
    return this.test({
      name: 'iban',
      exclusive: true,
      message: msg || 'El IBAN no es válido',
      test: (value) => isValidIBANControlDigit(value),
    })
  }

  bankAccountControlDigit(msg?: string) {
    return this.test({
      name: 'ccc',
      exclusive: true,
      message: msg || 'La cuenta bancaria no es válida',
      test: (value) => isValidBankAccountControlDigit(value),
    })
  }

  postalCode(msg?: string) {
    return this.test({
      name: 'postalCode',
      exclusive: true,
      message: msg || 'El código postal no es válido',
      test: (value) => POSTAL_CODE_REGEX.test(value),
    })
  }

  // Reject emails ends in '.con' and emails with only one char in domain name
  strongEmail(msg?: string) {
    return this.test({
      name: 'strongEmail',
      exclusive: true,
      message: msg || 'El correo electrónico no es válido',
      test: (value) => EMAIL_REGEX.test(value),
    })
  }

  onlyHasNumber(msg?: string) {
    return this.test({
      name: 'onlyHasNumber',
      exclusive: true,
      message: msg || 'No puedes introducir letras',
      test: (value) => value === undefined || NUMBER_REGEX.test(value),
    })
  }
}
